import { useCallback, useLayoutEffect, useRef } from "react";

/**
 * Drives the shared mobile full-screen "push" transition: a slide-in on mount
 * and a matching reverse slide-out when the user goes back. Used by the order
 * detail, product detail, and product edit screens so opening and closing feel
 * symmetric.
 *
 * `close()` plays the reverse animation, then calls `onExit` (which unmounts /
 * navigates). The slide-in uses `fill: "none"` so no transform lingers once it
 * finishes — a persistent transform would break any `position: sticky` header
 * inside the screen.
 */
/** App-wide RTL check: the locale sets `dir` on <html> (see i18n intl). Reading
 *  it directly is more reliable than getComputedStyle on a freshly-mounted node. */
function isRtl(): boolean {
  return typeof document !== "undefined" && document.documentElement.dir === "rtl";
}

const EASE_IN = "cubic-bezier(0.22, 1, 0.36, 1)";
const EASE_OUT = "cubic-bezier(0.4, 0, 1, 1)";
const IN_MS = 300;
const OUT_MS = 240;

export function useSlideScreen<T extends HTMLElement = HTMLDivElement>(
  onExit: () => void,
  options?: { skipEnter?: boolean },
) {
  const ref = useRef<T>(null);
  const closingRef = useRef(false);
  const onExitRef = useRef(onExit);
  onExitRef.current = onExit;
  const skipEnter = options?.skipEnter ?? false;

  useLayoutEffect(() => {
    if (skipEnter) return;
    const el = ref.current;
    if (!el || typeof el.animate !== "function") return;
    const rtl = isRtl();
    const anim = el.animate(
      [
        { transform: rtl ? "translateX(-100%)" : "translateX(100%)" },
        { transform: "translateX(0)" },
      ],
      { duration: IN_MS, easing: EASE_IN, fill: "none" },
    );
    return () => anim.cancel();
  }, []);

  const close = useCallback(() => {
    if (closingRef.current) return;
    closingRef.current = true;
    const el = ref.current;
    if (!el || typeof el.animate !== "function") {
      onExitRef.current();
      return;
    }
    const rtl = isRtl();
    const anim = el.animate(
      [
        { transform: "translateX(0)" },
        { transform: rtl ? "translateX(-100%)" : "translateX(100%)" },
      ],
      { duration: OUT_MS, easing: EASE_OUT, fill: "forwards" },
    );
    const done = () => onExitRef.current();
    anim.onfinish = done;
    anim.oncancel = done;
  }, []);

  return { ref, close };
}
